/**
 * ASTRA API Integration Layer
 * ============================
 *
 * Comprehensive API client for connecting React dashboard to ASTRA backend
 * Handles authentication, real-time data, AI predictions, and all ASTRA services
 *
 * 2025-12-31: Added dual-mode support (Local + Cloudflare fallback)
 * - When ASTRA backend is online: Uses localhost:8001 (real-time)
 * - When ASTRA backend is offline: Falls back to Cloudflare D1 cache
 */

import axios from 'axios';

// Configuration - Local ASTRA backend
const LOCAL_API_URL = 'http://localhost:8001/api/v1';
const LOCAL_WS_URL = 'ws://localhost:8001/api/v1/events/ws';

// Cloudflare fallback (set via environment variable in production)
const CLOUDFLARE_API_URL = import.meta.env.VITE_CLOUDFLARE_API_URL || '';

// Legacy export for backward compatibility
const API_BASE_URL = LOCAL_API_URL;
const WS_BASE_URL = LOCAL_WS_URL;

class AstraAPI {
  constructor() {
    // Primary client - Local ASTRA backend
    this.client = axios.create({
      baseURL: LOCAL_API_URL,
      timeout: 5000, // Reduced timeout for faster fallback
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Fallback client - Cloudflare (only if configured)
    this.cloudflareClient = CLOUDFLARE_API_URL ? axios.create({
      baseURL: CLOUDFLARE_API_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    }) : null;

    // Track backend availability
    this.isLocalAvailable = true;
    this.lastLocalCheck = null;
    this.localCheckInterval = 30000; // Re-check local every 30s

    this.wsConnections = new Map();
    this.subscribers = new Map();
    this.authToken = null;
    this.dataSource = 'local'; // 'local' or 'cloudflare'

    // Request interceptor for auth
    this.client.interceptors.request.use(
      (config) => {
        if (this.authToken) {
          config.headers.Authorization = `Bearer ${this.authToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.handleAuthError();
        }
        return Promise.reject(error);
      }
    );
  }

  // ==========================================================================
  // DUAL-MODE API: Try local first, fallback to Cloudflare if offline
  // ==========================================================================

  /**
   * Smart request with automatic fallback to Cloudflare
   * @param {string} localPath - Path on local ASTRA backend
   * @param {string} cloudflarePath - Path on Cloudflare Worker (optional, defaults to localPath)
   * @param {object} options - Additional options
   * @returns {Promise<{data: any, source: string}>}
   */
  async getWithFallback(localPath, cloudflarePath = null, options = {}) {
    const cfPath = cloudflarePath || `/api${localPath}`;

    // Check if we should retry local backend
    const now = Date.now();
    if (!this.isLocalAvailable && this.lastLocalCheck) {
      if (now - this.lastLocalCheck > this.localCheckInterval) {
        this.isLocalAvailable = true; // Allow retry
      }
    }

    // Try local backend first
    if (this.isLocalAvailable) {
      try {
        const response = await this.client.get(localPath, options);
        this.dataSource = 'local';
        return { data: response.data, source: 'local', cached: false };
      } catch (error) {
        // Local failed, mark as unavailable and try Cloudflare
        this.isLocalAvailable = false;
        this.lastLocalCheck = now;
        console.log('Local backend offline, switching to Cloudflare cache');
      }
    }

    // Fallback to Cloudflare
    if (this.cloudflareClient) {
      try {
        const response = await this.cloudflareClient.get(cfPath, options);
        this.dataSource = 'cloudflare';
        return {
          data: response.data,
          source: 'cloudflare',
          cached: true,
          cached_at: response.data?.cached_at || null
        };
      } catch (error) {
        console.error('Both local and Cloudflare failed:', error);
        throw new Error('No backend available');
      }
    }

    throw new Error('Local backend offline and Cloudflare not configured');
  }

  /**
   * Get current data source (for UI indicator)
   */
  getDataSource() {
    return {
      source: this.dataSource,
      isLive: this.dataSource === 'local',
      isCached: this.dataSource === 'cloudflare',
      cloudflareConfigured: !!this.cloudflareClient
    };
  }

  // Authentication
  async authenticate(credentials) {
    try {
      const response = await this.client.post('/auth/login', credentials);
      this.authToken = response.data.token;
      localStorage.setItem('astra_token', this.authToken);
      return response.data;
    } catch (error) {
      throw new Error(`Authentication failed: ${error.message}`);
    }
  }

  logout() {
    this.authToken = null;
    localStorage.removeItem('astra_token');
    this.disconnectAllWebSockets();
  }

  // Portfolio API - Use your existing unified portfolio API
  async getPortfolioSummary(portfolioId = 1453) {
    const response = await this.client.get(`/portfolio/snapshot`);
    return response.data;
  }

  async getPortfolioAnalytics(portfolioId = 1453) {
    const response = await this.client.get(`/portfolio/positions`);
    return response.data;
  }

  async getTradeHistory(portfolioId = 1453, limit = 100) {
    const response = await this.client.get(`/portfolio/trades?limit=${limit}`);
    return response.data;
  }

  // 🔧 PRODUCTION ADDITION (2025-10-27): Portfolio Performance Metrics API
  async getPortfolioPerformance(portfolioId = 1453) {
    const response = await this.client.get(`/portfolio/performance/overview?portfolio_id=${portfolioId}`);
    return response.data;
  }

  async getPerformanceTimeSeries(portfolioId = 1453, timeframe = 'daily', days = 30) {
    const response = await this.client.get(`/portfolio/performance/timeseries?portfolio_id=${portfolioId}&timeframe=${timeframe}&days=${days}`);
    return response.data;
  }

  async getAssetBreakdown(portfolioId = 1453) {
    const response = await this.client.get(`/portfolio/performance/asset-breakdown?portfolio_id=${portfolioId}`);
    return response.data;
  }

  async getStrategyBreakdown(portfolioId = 1453) {
    const response = await this.client.get(`/portfolio/performance/strategy-breakdown?portfolio_id=${portfolioId}`);
    return response.data;
  }

  async getRiskMetrics(portfolioId = 1453) {
    const response = await this.client.get(`/portfolio/performance/risk-metrics?portfolio_id=${portfolioId}`);
    return response.data;
  }

  async getPortfolioStatistics(portfolioId = 1453) {
    const response = await this.client.get(`/portfolio/performance/statistics?portfolio_id=${portfolioId}`);
    return response.data;
  }

  async getCurrencyBreakdown(portfolioId = 1453) {
    const response = await this.client.get(`/portfolio/performance/currency-breakdown?portfolio_id=${portfolioId}`);
    return response.data;
  }

  // CSV Data Access - Read from centralized API endpoint
  async getTradeDataCSV() {
    try {
      // Use the centralized API endpoint to get CSV data
      const response = await this.client.get('/portfolio/csv/trade_data');
      if (response.data && response.data.data) {
        return this.parseCSV(response.data.data);
      } else {
        throw new Error('No CSV data received');
      }
    } catch (error) {
      console.warn('CSV API not available, using trade history API:', error);
      // Fallback to API data if CSV is not accessible
      return await this.getTradeHistory();
    }
  }

  parseCSV(csvText) {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',');
    return lines.slice(1).map(line => {
      const values = line.split(',');
      const obj = {};
      headers.forEach((header, index) => {
        obj[header.trim()] = values[index]?.trim() || '';
      });
      return obj;
    });
  }

  // Oracle API - AI Predictions (match your existing API structure)
  async getOraclePredictions(symbol, timeframe = '1hour') {
    const requestData = {
      symbol: symbol.toUpperCase(),
      timeframes: [timeframe],
      confidence_threshold: 0.70,
      include_quantum: true,
      include_ensemble: true,
      max_predictions: 11,
      source: 'dashboard'
    };

    const response = await this.client.post(`/oracle/predict`, requestData);
    return response.data;
  }

  async getMultiTimeframePredictions(symbol) {
    const timeframes = ['15min', '1hour', '4hour', '24hour', '7day'];
    const requestData = {
      symbol: symbol.toUpperCase(),
      timeframes: timeframes,
      confidence_threshold: 0.70,
      include_quantum: true,
      include_ensemble: true,
      max_predictions: 11,
      source: 'dashboard'
    };

    const response = await this.client.post(`/oracle/predict`, requestData);
    return response.data.predictions || [];
  }

  // Market Data API - Use your existing portfolio market data endpoint
  async getMarketData(symbols = []) {
    try {
      const response = await this.client.get('/portfolio/market-data');
      return response.data;
    } catch (error) {
      console.error('Market data API not available:', error);
      throw error;
    }
  }

  async getWatchlistData() {
    try {
      const response = await this.client.get('/oracle/watchlist');
      return response.data;
    } catch (error) {
      console.error('Watchlist API not available:', error);
      throw error;
    }
  }

  // 🔧 PRODUCTION ADDITION (2025-12-30): Comprehensive Watchlist Status API
  // Provides visibility into both Dynamic (Ultra-Fast) and Catalyst (AI Pipeline) watchlists
  async getWatchlistStatus() {
    try {
      const response = await this.client.get('/oracle/watchlist/status');
      return response.data;
    } catch (error) {
      console.error('Watchlist status API not available:', error);
      throw error;
    }
  }

  // AI System Status
  async getAISystemStatus() {
    const response = await this.client.get('/ai/status');
    return response.data;
  }

  // 🔧 PRODUCTION FIX (2025-12-28): Fixed endpoint to match backend route
  async getMultiAgentStatus() {
    const response = await this.client.get('/agents/status');
    return response.data;
  }

  // 🔧 PRODUCTION FIX (2025-12-28): Added missing method for Multi-Agent performance
  async getMultiAgentPerformance() {
    const response = await this.client.get('/agents/performance');
    return response.data;
  }

  async getAdvancedAIFeatures() {
    const response = await this.client.get('/ai/features');
    return response.data;
  }

  // Voice Interface (VAMCC)
  // 🔧 PRODUCTION FIX (2025-12-28): Fixed endpoints to match backend voice_routes.py
  async sendVoiceCommand(command) {
    // Backend expects text input via /voice/process endpoint
    const response = await this.client.post('/voice/process', {
      text: command,
      include_audio_response: false
    });
    return response.data;
  }

  async getVoiceStatus() {
    // Backend has /voice/stats endpoint for status
    const response = await this.client.get('/voice/stats');
    return response.data;
  }

  async getVoiceHealth() {
    // Check voice engine health
    const response = await this.client.get('/voice/health');
    return response.data;
  }

  // 🔧 PRODUCTION ADDITION (2025-10-27): Trading Orders & Execution Management API
  async getTradingOrders(status = 'all', limit = 100, portfolioId = 1453) {
    const response = await this.client.get(`/trading/orders?status=${status}&limit=${limit}&portfolio_id=${portfolioId}`);
    return response.data;
  }

  async getActiveOrders(portfolioId = 1453) {
    const response = await this.client.get(`/trading/orders/active?portfolio_id=${portfolioId}`);
    return response.data;
  }

  async getRecentOrders(limit = 20, portfolioId = 1453) {
    const response = await this.client.get(`/trading/orders/recent?limit=${limit}&portfolio_id=${portfolioId}`);
    return response.data;
  }

  async getExecutionMetrics(portfolioId = 1453, days = 30) {
    const response = await this.client.get(`/trading/execution/metrics?portfolio_id=${portfolioId}&days=${days}`);
    return response.data;
  }

  async getExecutionPerformance(portfolioId = 1453, days = 7) {
    const response = await this.client.get(`/trading/execution/performance?portfolio_id=${portfolioId}&days=${days}`);
    return response.data;
  }

  async getStrategyPerformance(portfolioId = 1453) {
    const response = await this.client.get(`/trading/strategies/performance?portfolio_id=${portfolioId}`);
    return response.data;
  }

  // Ultra-Fast Trading
  async getUltraFastTradingMetrics() {
    const response = await this.client.get('/trading/ultra-fast/metrics');
    return response.data;
  }

  // 🔧 PRODUCTION ADDITION (2025-10-27): System Configuration API
  async getSystemConfig() {
    const response = await this.client.get('/system/config');
    return response.data;
  }

  async getSystemStatus() {
    const response = await this.client.get('/system/status');
    return response.data;
  }

  async getTradingMode() {
    const response = await this.client.get('/system/trading-mode');
    return response.data;
  }

  // 🔧 PRODUCTION ADDITION (2025-10-31): API Services Health
  // Uses existing /health/api-services endpoint
  async getAPIServicesHealth() {
    try {
      const response = await this.client.get('/health/api-services');
      return response.data;
    } catch (error) {
      console.error('API services health check failed:', error);
      throw error;
    }
  }

  // 🔧 PRODUCTION ADDITION (2025-10-31): AI Signals/Decisions
  // Uses existing /agents/decisions endpoint (connects to AIDecisions database model)
  async getAISignals(limit = 10) {
    try {
      const response = await this.client.get(`/agents/decisions?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch AI signals:', error);
      throw error;
    }
  }

  // 🔧 PRODUCTION ADDITION (2025-10-31): Oracle Status
  // Uses existing /oracle/status endpoint
  async getOracleStatus() {
    try {
      const response = await this.client.get('/oracle/status');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch Oracle status:', error);
      throw error;
    }
  }

  // 🔧 PRODUCTION ADDITION (2025-10-31): Oracle History
  // Uses existing /oracle/history endpoint
  async getOracleHistory(symbol = null, limit = 100) {
    try {
      const url = symbol 
        ? `/oracle/history?symbol=${symbol}&limit=${limit}`
        : `/oracle/history?limit=${limit}`;
      const response = await this.client.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch Oracle history:', error);
      throw error;
    }
  }

  async setTradingMode(mode) {
    const response = await this.client.post('/system/trading-mode', { mode });
    return response.data;
  }

  // System Health - Use your existing health endpoints
  async getSystemHealth() {
    try {
      const response = await this.client.get('/health');
      return response.data;
    } catch (error) {
      console.error('System health API not available:', error);
      throw error;
    }
  }

  async getPerformanceMetrics() {
    try {
      const response = await this.client.get('/health/metrics');
      return response.data;
    } catch (error) {
      console.error('Performance metrics API not available:', error);
      throw error;
    }
  }

  // Social Trading / Telegram
  async getSocialTradingData() {
    const response = await this.client.get('/social/trading');
    return response.data;
  }

  async getTelegramStats() {
    const response = await this.client.get('/telegram/stats');
    return response.data;
  }

  // WebSocket Connections
  connectWebSocket(endpoint, onMessage, onError, onClose) {
    const wsUrl = `${WS_BASE_URL}${endpoint}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log(`WebSocket connected: ${endpoint}`);
      this.wsConnections.set(endpoint, ws);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      onMessage(data);
    };

    ws.onerror = (error) => {
      console.error(`WebSocket error: ${endpoint}`, error);
      if (onError) onError(error);
    };

    ws.onclose = () => {
      console.log(`WebSocket closed: ${endpoint}`);
      this.wsConnections.delete(endpoint);
      if (onClose) onClose();
    };

    return ws;
  }

  // Real-time Updates - Use your existing WebSocket endpoint
  subscribeToRealTimeUpdates(callback) {
    return this.connectWebSocket(
      '/events/ws',  // Your existing WebSocket endpoint
      callback,
      (error) => console.error('Real-time WebSocket error:', error),
      () => {
        // Auto-reconnect logic
        setTimeout(() => {
          this.subscribeToRealTimeUpdates(callback);
        }, 5000);
      }
    );
  }

  // Legacy methods for backward compatibility - all use the same WebSocket
  subscribeToPortfolioUpdates(portfolioId = 1, callback) {
    return this.subscribeToRealTimeUpdates((data) => {
      if (data.type === 'portfolio' || data.type === 'trade') {
        callback(data);
      }
    });
  }

  subscribeToMarketData(symbols = [], callback) {
    return this.subscribeToRealTimeUpdates((data) => {
      if (data.type === 'market' || data.type === 'price') {
        callback(data);
      }
    });
  }

  subscribeToAIPredictions(callback) {
    return this.subscribeToRealTimeUpdates((data) => {
      if (data.type === 'oracle' || data.type === 'prediction') {
        callback(data);
      }
    });
  }

  subscribeToTradingActivity(callback) {
    return this.subscribeToRealTimeUpdates((data) => {
      if (data.type === 'trade' || data.type === 'execution') {
        callback(data);
      }
    });
  }

  // Utility methods
  disconnectAllWebSockets() {
    this.wsConnections.forEach((ws, endpoint) => {
      ws.close();
      console.log(`Disconnected WebSocket: ${endpoint}`);
    });
    this.wsConnections.clear();
  }

  handleAuthError() {
    this.logout();
    // Redirect to login page or show auth error
    window.location.href = '/login';
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.client.get('/health');
      return response.data;
    } catch (error) {
      return { status: 'error', message: error.message };
    }
  }
}

// Create singleton instance
export const astraAPI = new AstraAPI();
export default astraAPI;
